import React, { useState, useEffect } from 'react';
import {
  MessageSquare, Mail, Building2, Calendar, Clock,
  CheckCircle, Phone, X, ChevronDown, User, Filter
} from 'lucide-react';
import { consultationService } from '../../services/consultation.service';
import { ConsultationRequest, ConsultationStatus } from '../../types/consultation.types';
import { formatSafeDate } from '../../utils/dateUtils';
import toast from 'react-hot-toast';

const statusColors: Record<ConsultationStatus, string> = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-yellow-100 text-yellow-800',
  scheduled: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800'
};

const statusLabels: Record<ConsultationStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  scheduled: 'Scheduled',
  completed: 'Completed',
  closed: 'Closed'
};

const ConsultRequestsPage: React.FC = () => {
  const [requests, setRequests] = useState<ConsultationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<ConsultationRequest | null>(null);
  const [statusFilter, setStatusFilter] = useState<ConsultationStatus | 'all'>('all');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const data = await consultationService.getAllRequests();
      setRequests(data);
    } catch (error) {
      console.error('Error loading consultation requests:', error);
      toast.error('Failed to load consultation requests');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (requestId: string, newStatus: ConsultationStatus) => {
    try {
      setUpdatingStatus(true);
      await consultationService.updateStatus(requestId, newStatus);
      await loadRequests();
      if (selectedRequest?.id === requestId) {
        setSelectedRequest(prev => prev ? { ...prev, status: newStatus } : null);
      }
      toast.success(`Status updated to ${statusLabels[newStatus]}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const filteredRequests = statusFilter === 'all'
    ? requests
    : requests.filter(r => r.status === statusFilter);

  const newCount = requests.filter(r => r.status === 'new').length;

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Consultation Requests</h1>
          <p className="text-gray-600 mt-1">
            Manage incoming consultation requests from the website
          </p>
        </div>
        {newCount > 0 && (
          <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-lg font-medium">
            {newCount} new request{newCount !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-4">
          <Filter className="w-5 h-5 text-gray-400" />
          <span className="text-sm font-medium text-gray-700">Filter by status:</span>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                statusFilter === 'all'
                  ? 'bg-gray-800 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All ({requests.length})
            </button>
            {(Object.keys(statusLabels) as ConsultationStatus[]).map(status => {
              const count = requests.filter(r => r.status === status).length;
              return (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    statusFilter === status
                      ? 'bg-gray-800 text-white'
                      : `${statusColors[status]} hover:opacity-80`
                  }`}
                >
                  {statusLabels[status]} ({count})
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Request List */}
        <div className="lg:col-span-2 space-y-4">
          {filteredRequests.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {statusFilter === 'all'
                  ? 'No consultation requests yet'
                  : `No ${statusLabels[statusFilter as ConsultationStatus].toLowerCase()} requests`}
              </p>
            </div>
          ) : (
            filteredRequests.map(request => (
              <div
                key={request.id}
                onClick={() => setSelectedRequest(request)}
                className={`bg-white rounded-lg shadow-sm border-2 p-4 cursor-pointer transition-all hover:shadow-md ${
                  selectedRequest?.id === request.id
                    ? 'border-accent-500'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-gray-900">{request.name}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[request.status]}`}>
                        {statusLabels[request.status]}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                      <span className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        {request.organization}
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {request.email}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm line-clamp-2">{request.message}</p>
                  </div>
                  <div className="text-right text-xs text-gray-500 ml-4">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatSafeDate(request.createdAt, 'MMM d, yyyy')}
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3" />
                      {formatSafeDate(request.createdAt, 'h:mm a')}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Detail Panel */}
        <div className="lg:col-span-1">
          {selectedRequest ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 sticky top-6">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Request Details</h3>
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 space-y-4">
                {/* Contact Info */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <User className="w-5 h-5 text-gray-400" />
                    <span className="font-medium text-gray-900">{selectedRequest.name}</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="w-4 h-4" />
                      <a href={`mailto:${selectedRequest.email}`} className="text-accent-600 hover:underline">
                        {selectedRequest.email}
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Building2 className="w-4 h-4" />
                      {selectedRequest.organization}
                    </div>
                  </div>
                </div>

                {/* Status Update */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <div className="relative">
                    <select
                      value={selectedRequest.status}
                      onChange={(e) => handleStatusChange(selectedRequest.id, e.target.value as ConsultationStatus)}
                      disabled={updatingStatus}
                      className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                    >
                      {(Object.keys(statusLabels) as ConsultationStatus[]).map(status => (
                        <option key={status} value={status}>
                          {statusLabels[status]}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">
                    {selectedRequest.message}
                  </div>
                </div>

                {/* Timestamps */}
                <div className="text-xs text-gray-500 space-y-1 pt-2 border-t border-gray-200">
                  <div>Submitted: {formatSafeDate(selectedRequest.createdAt, 'MMM d, yyyy h:mm a')}</div>
                  {selectedRequest.contactedAt && (
                    <div>Contacted: {formatSafeDate(selectedRequest.contactedAt, 'MMM d, yyyy h:mm a')}</div>
                  )}
                  {selectedRequest.scheduledAt && (
                    <div>Scheduled: {formatSafeDate(selectedRequest.scheduledAt, 'MMM d, yyyy h:mm a')}</div>
                  )}
                  {selectedRequest.completedAt && (
                    <div>Completed: {formatSafeDate(selectedRequest.completedAt, 'MMM d, yyyy h:mm a')}</div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="pt-2 border-t border-gray-200 space-y-2">
                  <a
                    href={`mailto:${selectedRequest.email}?subject=Re: Consultation Request - Canvassed`}
                    className="w-full flex items-center justify-center gap-2 bg-accent-600 hover:bg-accent-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    Send Email
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Select a request to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsultRequestsPage;
